import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  ContentRow,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';

const Workflows = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  
  // Mock data for workflows
  const workflows = [
    { 
      id: 'WF-001', 
      name: 'Claims Processing', 
      description: 'End-to-end workflow for processing insurance claims',
      steps: 8,
      agents: ['Claims Analyst', 'Document Processor', 'Fraud Investigator'],
      status: 'active',
      lastRun: '2025-03-15 14:32',
      avgDuration: '4m 12s',
      successRate: 94
    },
    { 
      id: 'WF-002', 
      name: 'Underwriting Review', 
      description: 'Risk assessment and policy pricing workflow',
      steps: 6,
      agents: ['Underwriter Assistant', 'Document Processor', 'Risk Assessor'],
      status: 'active',
      lastRun: '2025-03-15 13:45',
      avgDuration: '8m 30s',
      successRate: 96
    },
    { 
      id: 'WF-003', 
      name: 'Customer Onboarding', 
      description: 'New customer registration and verification process',
      steps: 5,
      agents: ['Customer Service', 'Document Processor'],
      status: 'active',
      lastRun: '2025-03-15 12:18',
      avgDuration: '3m 45s',
      successRate: 98
    },
    { 
      id: 'WF-004', 
      name: 'Fraud Investigation', 
      description: 'Deep analysis of potentially fraudulent activities',
      steps: 7,
      agents: ['Fraud Investigator', 'Claims Analyst', 'Document Processor'],
      status: 'paused',
      lastRun: '2025-03-14 16:22',
      avgDuration: '12m 08s',
      successRate: 92
    },
    { 
      id: 'WF-005', 
      name: 'Policy Renewal', 
      description: 'Automated policy renewal and updates workflow',
      steps: 4,
      agents: ['Underwriter Assistant', 'Customer Service'],
      status: 'draft',
      lastRun: 'Never',
      avgDuration: 'N/A',
      successRate: 'N/A'
    },
  ];
  
  // Filter workflows based on search term and active tab
  const filteredWorkflows = workflows.filter(workflow => 
    (workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workflow.description.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (activeTab === 'all' || workflow.status === activeTab)
  );
  
  // Get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'active';
      case 'paused': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };
  
  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeader>
          <PageTitle><PageIcon>⌲</PageIcon> Workflows</PageTitle>
          <Button primary>Create Workflow</Button>
        </PageHeader>
        
        <PageDescription>
          Design, deploy, and monitor automated workflows that orchestrate multiple AI agents to complete complex business processes.
          Create end-to-end automation for claims processing, underwriting, customer service, and more.
        </PageDescription>
        
        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'active'} onClick={() => setActiveTab('active')}>Active</Tab>
            <Tab active={activeTab === 'paused'} onClick={() => setActiveTab('paused')}>Paused</Tab>
            <Tab active={activeTab === 'draft'} onClick={() => setActiveTab('draft')}>Drafts</Tab>
            <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All</Tab>
          </TabList>
          
          <ActionBar>
            <SearchInput 
              type="text" 
              placeholder="Search workflows..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button>Filter</Button>
            <Button>Sort</Button>
          </ActionBar>
          
          <Card>
            <CardTitle>Workflow Library</CardTitle>
            <Table>
              <thead>
                <tr>
                  <TableHeader>ID</TableHeader>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Description</TableHeader>
                  <TableHeader>Steps</TableHeader>
                  <TableHeader>Agents</TableHeader>
                  <TableHeader>Last Run</TableHeader>
                  <TableHeader>Avg. Duration</TableHeader>
                  <TableHeader>Success Rate</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredWorkflows.map(workflow => (
                  <TableRow key={workflow.id}>
                    <TableCell>{workflow.id}</TableCell>
                    <TableCell>{workflow.name}</TableCell>
                    <TableCell>{workflow.description}</TableCell>
                    <TableCell>{workflow.steps}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {workflow.agents.map((agent, index) => (
                          <span 
                            key={index}
                            style={{ 
                              background: 'rgba(0, 102, 255, 0.1)', 
                              color: 'var(--primary)',
                              padding: '0.2rem 0.4rem',
                              borderRadius: '20px',
                              fontSize: '0.7rem'
                            }}
                          >
                            {agent}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{workflow.lastRun}</TableCell>
                    <TableCell>{workflow.avgDuration}</TableCell>
                    <TableCell>
                      {typeof workflow.successRate === 'number' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>{workflow.successRate}%</span>
                          <div style={{ width: '50px', height: '6px', background: 'rgba(0,102,255,0.1)', borderRadius: '3px' }}>
                            <div 
                              style={{ 
                                width: `${workflow.successRate}%`, 
                                height: '6px', 
                                background: workflow.successRate >= 95 ? '#34C759' : workflow.successRate >= 90 ? 'var(--primary)' : '#FFCC00', 
                                borderRadius: '3px' 
                              }}
                            ></div>
                          </div>
                        </div>
                      ) : workflow.successRate}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={getStatusColor(workflow.status)}>
                        {workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Edit</Button>
                        {workflow.status === 'active' ? (
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Run</Button>
                        ) : workflow.status === 'paused' ? (
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Resume</Button>
                        ) : (
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Activate</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </Card>
          
          <ContentGrid style={{ marginTop: '2rem' }}>
            <Card>
              <CardTitle>Workflow Performance</CardTitle>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                  {workflows
                    .filter(wf => wf.status === 'active')
                    .map(workflow => (
                      <div key={workflow.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                          <span>{workflow.name}</span>
                          <span>{workflow.successRate}%</span>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: 'rgba(0,102,255,0.1)', borderRadius: '4px' }}>
                          <div 
                            style={{ 
                              width: `${workflow.successRate}%`, 
                              height: '8px', 
                              background: workflow.successRate >= 95 ? '#34C759' : workflow.successRate >= 90 ? 'var(--primary)' : '#FFCC00', 
                              borderRadius: '4px' 
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardTitle>Workflow Activity</CardTitle>
              <CardContent>
                <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: '20px' }}>
                  {[42, 56, 38, 64, 72, 58, 68].map((value, index) => (
                    <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ height: `${value * 2}px`, width: '100%', background: 'var(--primary)', borderRadius: '4px 4px 0 0' }}></div>
                      <div style={{ fontSize: '0.7rem', marginTop: '5px' }}>{`D${index + 1}`}</div>
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Workflow Executions - Last 7 Days
                </div>
              </CardContent>
            </Card>
          </ContentGrid>
        </TabContainer>
        
        <Card style={{ marginTop: '2rem' }}>
          <CardTitle>Workflow Designer</CardTitle>
          <CardContent>
            <div style={{ 
              height: '300px', 
              background: 'rgba(0, 102, 255, 0.05)', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '2px dashed rgba(0, 102, 255, 0.2)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{ fontSize: '3rem', color: 'var(--primary)' }}>⌲</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>Visual Workflow Designer</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', maxWidth: '500px', textAlign: 'center' }}>
                Create and edit workflows using our drag-and-drop interface. Connect agents, define conditions, and set up triggers to automate complex business processes.
              </div>
              <Button primary style={{ marginTop: '1rem' }}>Open Designer</Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default Workflows;
